/**
 * safeExternalUrl — validate a URL before it goes into an `href`.
 *
 * WHY THIS EXISTS
 * ---------------
 * The admin form lets an operator paste a URL into a text field, and the value
 * is stored in the DB and later rendered as an anchor href. Without
 * validation, a `javascript:` URL would become a live XSS vector on the
 * public site — an operator mistake (or a compromised admin session) would
 * execute script in every visitor's browser.
 *
 * The rules are deliberately strict:
 *
 *   - Only `http:` and `https:` are accepted. `javascript:`, `data:`,
 *     `vbscript:` and `file:` are all rejected.
 *   - A protocol-relative value ("//evil.com") is rejected, because it
 *     inherits the page's scheme from an untrusted source and, on an
 *     `http:` page, would silently downgrade to plaintext.
 *   - A bare host ("www.foo.com") is rejected — it is not a valid href
 *     without a scheme, and browsers resolve it relative to the current
 *     path, which is never what the operator meant.
 *
 * Returns the normalised URL string, or undefined when the value is missing
 * or unsafe. Callers render plain text in that case, so a bad value degrades
 * to "no link" rather than to "broken link" or "executable link".
 */
export function safeExternalUrl(url?: string | null): string | undefined {
  if (!url) return undefined
  const trimmed = url.trim()
  if (!trimmed) return undefined
  // A protocol-relative URL parses fine under `new URL` only when given a
  // base, so it is rejected by the "no scheme" branch below. Check it
  // explicitly anyway so the intent is obvious to the next reader.
  if (trimmed.startsWith('//')) return undefined
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return undefined
    }
    return parsed.toString()
  } catch {
    return undefined
  }
}

/**
 * safeInternalLink — validate a link that may point off-site OR at a page
 * of this site.
 *
 * WHY THIS EXISTS
 * ---------------
 * `cms_products.cta_link` is the one CMS-controlled URL column that is
 * allowed to point INWARDS: the operator picks "go to another product" and
 * the column holds `/products/harbour`. It also holds the occasional
 * `mailto:` for the products whose call to action is "email us". So the
 * http(s)-only rule that `safeExternalUrl` enforces would refuse perfectly
 * good production rows — the write path would 400 on a row that is fine.
 *
 * The rules are:
 *
 *   - An absolute `http:` / `https:` URL is accepted, normalised through
 *     `new URL(v).href`.
 *   - A root-relative path is accepted and returned AS-IS: exactly one
 *     leading `/`, never `//` (protocol-relative) and never a backslash
 *     anywhere. A browser reads `\` as `/`, so `/\evil.com` is a
 *     protocol-relative URL in disguise.
 *   - `mailto:` and `tel:` are accepted with a non-empty rest, and are
 *     returned UNCHANGED — `new URL('mailto:info@yada.hk').href` rewrites
 *     the address into a form the operator never typed.
 *
 * Everything else is refused:
 *
 *   - `javascript:`, `data:`, `vbscript:`, `file:`, `blob:` — script-bearing
 *     or local-file schemes;
 *   - protocol-relative `//evil.example/x`, and its backslash disguise
 *     `/\evil.example/x`;
 *   - a relative path with no leading slash (`foo/bar`) — the browser would
 *     resolve it against the CURRENT path, which is never what the operator
 *     meant;
 *   - a query-only (`?q=1`) or fragment-only (`#x`) value;
 *   - anything `new URL()` cannot parse.
 *
 * Returns the value to put in the href, or undefined when it is missing or
 * unsafe — in which case the caller renders no link at all.
 */
export function safeInternalLink(url?: string | null): string | undefined {
  if (typeof url !== 'string') return undefined
  const trimmed = url.trim()
  if (!trimmed) return undefined
  // A backslash is a `/` to a browser, so it can smuggle a protocol-relative
  // URL past the leading-slash check below. Reject it outright.
  if (trimmed.includes('\\')) return undefined
  // `mailto:` / `tel:` are matched by hand: they are not URLs the browser
  // navigates to in the usual sense, and `new URL()` would rewrite them.
  if (/^(mailto|tel):.+$/i.test(trimmed)) return trimmed
  // Protocol-relative: it inherits the page's scheme from an untrusted
  // source and, on an `http:` page, silently downgrades to plaintext.
  if (trimmed.startsWith('//')) return undefined
  // A root-relative path is already site-rooted. It needs no
  // normalisation, and building a URL for it would invent a hostname.
  if (trimmed.startsWith('/')) return trimmed
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return undefined
    }
    return parsed.toString()
  } catch {
    return undefined
  }
}

/**
 * safeAssetPath — validate a path to a FILE the site serves (a PDF, a
 * photo).
 *
 * WHY THIS EXISTS
 * ---------------
 * `cms_magazines.pdf_path`, `cms_lectures.photo1_url` and
 * `cms_lectures.photo2_url` do not point at another website — they point at
 * a file on THIS site, so they are stored as root-relative paths
 * (`/media/…`, `/uploads/…`). The http(s)-only rule would refuse every one
 * of them, and an asset rule that only allowed absolute URLs would refuse
 * every one of them too. So this policy is the union of the two:
 *
 *   - An absolute `http:` / `https:` URL is accepted, normalised through
 *     `new URL(v).href` — a media library can hand out an absolute URL.
 *   - A root-relative path is accepted and returned AS-IS: exactly one
 *     leading `/`, never `//` (protocol-relative) and never a backslash
 *     anywhere. A browser reads `\` as `/`, so `/\evil.com` is a
 *     protocol-relative URL in disguise.
 *
 * Everything else is refused:
 *
 *   - `javascript:`, `data:`, `vbscript:`, `file:`, `blob:` — script-bearing
 *     or local-file schemes;
 *   - protocol-relative `//evil.example/x`, and its backslash disguise
 *     `/\evil.example/x`;
 *   - a relative path with no leading slash (`uploads/x.jpg`) — the browser
 *     would resolve it against the CURRENT path;
 *   - `mailto:` and `tel:` — an asset is not an address;
 *   - anything `new URL()` cannot parse.
 *
 * Returns the value to put in the href / src, or undefined when it is
 * missing or unsafe — in which case the caller renders no link and no image
 * at all, because a broken image is better than an executable one but no
 * image at all is better than a broken one.
 */
export function safeAssetPath(url?: string | null): string | undefined {
  if (typeof url !== 'string') return undefined
  const trimmed = url.trim()
  if (!trimmed) return undefined
  if (trimmed.includes('\\')) return undefined
  if (trimmed.startsWith('//')) return undefined
  if (trimmed.startsWith('/')) return trimmed
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return undefined
    }
    return parsed.toString()
  } catch {
    return undefined
  }
}
